import { zodResolver } from '@hookform/resolvers/zod'
import { useAtomValue } from 'jotai'
import React from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useAsyncFn } from 'react-use'
import * as R from 'remeda'
import { type Asyncify } from 'type-fest'

import {
  EditFilterDialog,
  type EditFilterDialogProps,
} from '~/components/features/EditFilterDialog'
import { SurveyFilterGroupsInput } from '~/components/features/SurveyFilterGroupsInput'
import { graphql } from '~/gql'
import { useGraphQLRequest } from '~/hooks/useGraphQLRequest'
import { useLogEvent } from '~/hooks/useLogEvent'
import { useTabulate } from '~/hooks/useTabulate'
import { exists, matchSurveyFilter } from '~/utils'

import {
  isVisibleEditDialogAtom,
  onChangeAtom,
  surveyIdAtom,
} from '../../atoms'
import {
  useMainQueryAvailableCustomerAttributeGroups,
  useMainQueryBrandGroups,
  useMainQueryQuestions,
} from '../../hooks/useMainQuery'
import {
  useInvalidateSurveyFiltersQuery,
  useSurveyFiltersQueryData,
} from '../../hooks/useSurveyFiltersQuery'

import { type InputValues, schema } from './utils'

const countTotalAnswersMutationDocument = graphql(`
  mutation SurveyFilterInformation_CountTotalAnswersMutation(
    $surveyId: String!
    $filter: [[CustomerAttributeFilterItemInput!]!]!
  ) {
    count: countTotalAnswers(
      surveyId: $surveyId
      customFilter: { filter: $filter }
    ) {
      awaitId
    }
  }
`)

const countTotalAnswersQueryDocument = graphql(`
  query SurveyFilterInformation_CountTotalAnswers($awaitId: String!) {
    count: countTotalAnswers(awaitId: $awaitId) {
      arrived
      result {
        count
      }
    }
  }
`)

const getSurveyFilterDocument = graphql(`
  query SurveyFilterInformation_GetSurveyFilter($surveyFilterId: ID!) {
    getSurveyFilter(surveyFilterId: $surveyFilterId) {
      id
      customFilter {
        filter {
          category
          question {
            questionId
            operation
            answerOptions {
              option
              text
            }
          }
          customerAttribute {
            customerAttributeIds
            operation
          }
          brandSegments {
            brandGroupId
            brandNumber
            segmentNumbers
          }
        }
      }
    }
  }
`)

const createSurveyFilterDocument = graphql(`
  mutation SurveyFilterInformation_CreateSurveyFilter(
    $surveyId: ID!
    $title: String!
    $filter: [[CustomerAttributeFilterItemInput!]!]!
  ) {
    createSurveyFilter(
      surveyId: $surveyId
      title: $title
      customFilter: { filter: $filter }
    ) {
      id
    }
  }
`)

const deleteSurveyFilterDocument = graphql(`
  mutation SurveyFilterInformation_DeleteSurveyFilter($surveyFilterId: ID!) {
    deleteSurveyFilter(surveyFilterId: $surveyFilterId) {
      result
    }
  }
`)

type FilterItem = InputValues['filterGroups'][number][number]

const resolver = zodResolver(schema)

const useLogSetFilterEvent = () => {
  const logEvent = useLogEvent()

  return React.useCallback(
    (filterGroups: InputValues['filterGroups']) => {
      logEvent('Set filter', {
        eventProperties: filterGroups
          .flatMap((filterGroup, filterGroupIndex) =>
            filterGroup.map((filter, filterIndex) => {
              const key = (name: string) =>
                `${filterGroupIndex + 1}_${filterIndex + 1}_${name}`

              return [
                { key: key('category'), value: filter.category },
                ...matchSurveyFilter(filter, {
                  question: ({ answerOptions, operation, questionId }) => [
                    { key: key('questionId'), value: questionId },
                    { key: key('operation'), value: operation },
                    {
                      key: key('answerOptions'),
                      value: answerOptions.map(R.prop('option')).join(),
                    },
                  ],
                  customerAttribute: ({ customerAttributeIds, operation }) => [
                    {
                      key: key('customerAttributeIds'),
                      value: customerAttributeIds.join(),
                    },
                    { key: key('operation'), value: operation },
                  ],
                  brandSegments: ({
                    brandGroupId,
                    brandNumber,
                    segmentNumbers,
                  }) => [
                    { key: key('brandGroupId'), value: `${brandGroupId}` },
                    { key: key('brandNumber'), value: `${brandNumber}` },
                    {
                      key: key('segmentNumbers'),
                      value: segmentNumbers.join(),
                    },
                  ],
                }),
              ]
            }),
          )
          .flat(),
        withSuffix: true,
      })
    },
    [logEvent],
  )
}

export type EditSurveyFilterDialogProps = {
  filterGroups: InputValues['filterGroups']
}

export const EditSurveyFilterDialog = ({
  filterGroups,
}: EditSurveyFilterDialogProps) => {
  const { t } = useTranslation()
  const methods = useForm<InputValues>({
    defaultValues: { filterGroups },
    resolver,
  })
  const { control } = methods

  const onChange = useAtomValue(onChangeAtom)
  const [_countTotalAnswersState, countTotalAnswers] = useTabulate(
    countTotalAnswersMutationDocument,
    countTotalAnswersQueryDocument,
  )
  const surveyId = useAtomValue(surveyIdAtom)
  const logSetFilterEvent = useLogSetFilterEvent()
  const [handleExecuteState, handleExecute] = useAsyncFn<
    Asyncify<EH<EditFilterDialogProps<FilterItem>, 'onExecute'>>
  >(
    async (filterGroups) => {
      if (!exists(filterGroups)) {
        onChange(undefined)

        return
      }

      const result = await countTotalAnswers({ surveyId, filter: filterGroups })

      onChange({ count: result?.count ?? NaN, filterGroups })

      logSetFilterEvent(filterGroups)
    },
    [countTotalAnswers, logSetFilterEvent, onChange, surveyId],
  )

  const brandGroups = useMainQueryBrandGroups()
  const customerAttributeGroups = useMainQueryAvailableCustomerAttributeGroups()
  const questions = useMainQueryQuestions()
  const surveyFilters = useSurveyFiltersQueryData()

  const graphQLRequest = useGraphQLRequest()
  const invalidateSurveyFiltersQuery = useInvalidateSurveyFiltersQuery()
  const handleDelete = React.useCallback<
    EH<EditFilterDialogProps<FilterItem>, 'onDelete'>
  >(
    async (surveyFilterId) => {
      await graphQLRequest({
        document: deleteSurveyFilterDocument,
        variables: { surveyFilterId },
      })

      await invalidateSurveyFiltersQuery()
    },
    [graphQLRequest, invalidateSurveyFiltersQuery],
  )

  const handleLoad = React.useCallback<
    EH<EditFilterDialogProps<FilterItem>, 'onLoad'>
  >(
    async (surveyFilterId) => {
      const result = await graphQLRequest({
        document: getSurveyFilterDocument,
        variables: { surveyFilterId },
      })
      const filterGroups = result.getSurveyFilter?.customFilter?.filter

      return filterGroups as InputValues['filterGroups']
    },
    [graphQLRequest],
  )

  const handleSave = React.useCallback<
    EH<EditFilterDialogProps<FilterItem>, 'onSave'>
  >(
    async (title, filterGroups) => {
      await graphQLRequest({
        document: createSurveyFilterDocument,
        variables: {
          surveyId,
          title,
          filter: filterGroups,
        },
      })

      await invalidateSurveyFiltersQuery()
    },
    [graphQLRequest, invalidateSurveyFiltersQuery, surveyId],
  )

  return (
    <EditFilterDialog
      executing={handleExecuteState.loading}
      filterGroupsInput={
        <SurveyFilterGroupsInput
          brandGroups={brandGroups}
          control={control}
          customerAttributeGroups={customerAttributeGroups}
          questions={questions}
        />
      }
      filters={surveyFilters}
      isVisibleEditDialogAtom={isVisibleEditDialogAtom}
      methods={methods}
      title={t('survey_filter_condition')}
      onDelete={handleDelete}
      onExecute={handleExecute}
      onLoad={handleLoad}
      onSave={handleSave}
    />
  )
}
